import { useState, useEffect } from 'react';

type ImageStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface UseImageResult {
  image: HTMLImageElement | null;
  status: ImageStatus;
  error: Error | null;
}

/**
 * Hook for loading images with status tracking.
 * Used by TemplateEditor for canvas-based image editing.
 */
export function useImage(src: string | null): UseImageResult {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [status, setStatus] = useState<ImageStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!src) {
      setImage(null);
      setStatus('idle');
      setError(null);
      return;
    }

    setStatus('loading');
    setError(null);

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      setImage(img);
      setStatus('loaded');
    };

    img.onerror = () => {
      setError(new Error(`Failed to load image: ${src}`));
      setStatus('error');
      setImage(null);
    };

    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return { image, status, error };
}
